import { TRANSFER_SEARCH_VERTICAL } from 'constants/experience'
import { RECENT_SEARCHES_LIMIT } from 'constants/search'
import { take, uniqueBy } from 'lib/array/arrayUtils'
import * as isomorphicLocalStorage from 'lib/storage/isomorphicLocalStorage'

const EXPERIENCES_RECENT_SEARCHES_KEY = 'experiences-recent-searches'

function getExperienceRecentSearchKey(search: App.FullRecentSearch): string {
  return `${search.vertical ?? 'experiences'}:${search.searchItem.value}`
}

function matchesRecentSearchVertical(
  search: App.FullRecentSearch,
  vertical?: string,
): boolean {
  if (vertical === TRANSFER_SEARCH_VERTICAL) {
    return search.vertical === TRANSFER_SEARCH_VERTICAL
  }

  return search.vertical !== TRANSFER_SEARCH_VERTICAL
}

function getExperienceRecentSearchId(searchItem: App.SearchItem, dates: App.SearchDates): string {
  return [
    dates.checkIn,
    dates.checkOut,
    dates.flexibleDuration,
    dates.flexibleMonths,
    searchItem.searchType,
    searchItem.value,
  ].join('-')
}

export function getLocalExperienceRecentSearches(): Array<App.FullRecentSearch> {
  const recentSearches = isomorphicLocalStorage.get(EXPERIENCES_RECENT_SEARCHES_KEY)
  return Array.isArray(recentSearches) ? recentSearches : []
}

export function getLocalExperiencesRecentSearches(): Array<App.FullRecentSearch> {
  return getLocalExperienceRecentSearches().filter(search => matchesRecentSearchVertical(search))
}

function setLocalExperienceRecentSearches(recentSearches: Array<App.FullRecentSearch>) {
  isomorphicLocalStorage.set(EXPERIENCES_RECENT_SEARCHES_KEY, recentSearches)
}

export function saveExperienceRecentSearch(
  searchItem: App.SearchItem,
  dates: App.SearchDates,
  rooms: Array<App.Occupants>,
  categories?: Array<number>,
  vertical?: string,
) {
  const recentSearch: App.FullRecentSearch = {
    searchItem,
    dates,
    rooms,
    categories,
    vertical,
    recentSearchId: getExperienceRecentSearchId(searchItem, dates),
    dateAdded: new Date().toISOString(),
  }

  const nextSearches = take(
    uniqueBy(
      [recentSearch, ...getLocalExperienceRecentSearches()],
      getExperienceRecentSearchKey,
    ),
    RECENT_SEARCHES_LIMIT,
  )

  setLocalExperienceRecentSearches(nextSearches)
}

export function clearLocalExperienceRecentSearches() {
  setLocalExperienceRecentSearches([])
}

export function clearLocalExperiencesRecentSearches() {
  setLocalExperienceRecentSearches(
    getLocalExperienceRecentSearches().filter(search => !matchesRecentSearchVertical(search)),
  )
}

export function deleteExperienceRecentSearch(recentSearch: App.FullRecentSearch) {
  const key = getExperienceRecentSearchKey(recentSearch)
  setLocalExperienceRecentSearches(
    getLocalExperienceRecentSearches().filter(search => getExperienceRecentSearchKey(search) !== key),
  )
}

export function bumpExperienceRecentSearch(recentSearch: App.FullRecentSearch) {
  const key = getExperienceRecentSearchKey(recentSearch)
  const nextSearches = [
    recentSearch,
    ...getLocalExperienceRecentSearches().filter(search => getExperienceRecentSearchKey(search) !== key),
  ]

  setLocalExperienceRecentSearches(take(nextSearches, RECENT_SEARCHES_LIMIT))
}

function useExperienceRecentSearches(vertical?: string): Array<App.FullRecentSearch> {
  return getLocalExperienceRecentSearches().filter(search => matchesRecentSearchVertical(search, vertical))
}

export default useExperienceRecentSearches
